"use client";

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

/**
 * Process source references in markdown content
 * Converts {source N} to clickable anchor links
 */
function processSourceReferences(content: string): string {
  // Replace {source N} with markdown link to #source-N
  return content.replace(/\{source\s+(\d+)\}/gi, (match, num) => {
    return `[(${num})](<#source-${num}>)`;
  });
}

export default function MarkdownRenderer({ content, className = "" }: MarkdownRendererProps) {
  // Preprocess content to handle source references
  const processedContent = processSourceReferences(content);
  
  return (
    <div className={`prose prose-gray max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="font-poppins text-3xl font-bold text-gray-900 mb-4 leading-tight">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="font-poppins text-2xl font-semibold text-gray-900 mb-3 mt-6 leading-tight">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="font-poppins text-xl font-semibold text-gray-900 mb-2 mt-5 leading-tight">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="font-poppins text-lg font-semibold text-gray-900 mb-2 mt-4 leading-tight">
              {children}
            </h4>
          ),
          h5: ({ children }) => (
            <h5 className="font-poppins text-base font-semibold text-gray-900 mb-2 mt-3 leading-tight">
              {children}
            </h5>
          ),
          h6: ({ children }) => (
            <h6 className="font-poppins text-sm font-semibold text-gray-900 mb-1 mt-3 leading-tight">
              {children}
            </h6>
          ),
          p: ({ children }) => (
            <p className="font-poppins text-base text-gray-700 mb-4 leading-relaxed">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc pl-6 mb-4 space-y-2">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-6 mb-4 space-y-2">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="font-poppins text-base text-gray-700 leading-relaxed">
              {children}
            </li>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-gray-300 pl-4 py-2 mb-4 bg-gray-50 rounded-r">
              <div className="font-poppins text-gray-700 italic">
                {children}
              </div>
            </blockquote>
          ),
          code: ({ children, ...props }) => {
            const inline = (props as { inline?: boolean }).inline;
            return inline ? (
              <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">
                {children}
              </code>
            ) : (
              <code className="block bg-gray-900 text-gray-100 p-4 rounded text-sm font-mono overflow-x-auto mb-4">
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <div className="mb-4">
              {children}
            </div>
          ),
          a: ({ href, children }) => {
            // Style source references differently
            const isSourceRef = href?.startsWith('#source-');
            
            return (
              <a
                href={href}
                className={
                  isSourceRef
                    ? "text-gray-600 hover:text-gray-900 no-underline font-medium transition-colors text-sm align-super"
                    : "text-blue-600 hover:text-blue-800 underline font-medium transition-colors"
                }
                target={href?.startsWith('http') ? '_blank' : undefined}
                rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              >
                {children}
              </a>
            );
          },
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-900">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic text-gray-700">
              {children}
            </em>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border-collapse border border-gray-300">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-gray-50">
              {children}
            </thead>
          ),
          th: ({ children }) => (
            <th className="border border-gray-300 px-4 py-2 text-left font-poppins font-semibold text-gray-900">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-gray-300 px-4 py-2 font-poppins text-gray-700">
              {children}
            </td>
          ),
          hr: () => (
            <hr className="border-t border-gray-300 my-6" />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}